import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getSupabaseClient } from '../../lib/supabase';

type ReviewInfo = {
  reviewer_name: string;
  guide_name: string;
};

type PageStatus = 'loading' | 'invalid' | 'expired' | 'already_submitted' | 'error' | 'ready' | 'submitting' | 'success';

export function OffPlatformReviewPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<PageStatus>('loading');
  const [reviewInfo, setReviewInfo] = useState<ReviewInfo | null>(null);
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const supabase = useMemo(() => {
    try {
      return getSupabaseClient();
    } catch (err) {
      console.error('Supabase client init failed', err);
      return null;
    }
  }, []);

  useEffect(() => {
    if (!token || !supabase) {
      setStatus('invalid');
      return;
    }

    let isActive = true;

    async function loadReviewInfo() {
      setStatus('loading');
      try {
        // For GET requests, we call the function URL directly with query params
        const response = await fetch(
          `${(window as any).__ENV__?.SUPABASE_URL || ''}/functions/v1/off-platform-review-submit?token=${token}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        const result = await response.json();

        if (!isActive) return;

        if (!response.ok) {
          if (result.error?.includes('expired')) {
            setStatus('expired');
          } else if (result.error?.includes('already been submitted')) {
            setStatus('already_submitted');
          } else if (result.error?.includes('not found')) {
            setStatus('invalid');
          } else {
            setStatus('error');
          }
          return;
        }

        setReviewInfo({
          reviewer_name: result.reviewer_name,
          guide_name: result.guide_name,
        });
        setStatus('ready');
      } catch (error) {
        console.error('Error loading review info:', error);
        if (isActive) setStatus('error');
      }
    }

    void loadReviewInfo();

    return () => {
      isActive = false;
    };
  }, [token, supabase]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (rating === 0) {
      setSubmitError('Please select a rating');
      return;
    }

    setStatus('submitting');

    try {
      const response = await fetch(
        `${(window as any).__ENV__?.SUPABASE_URL || ''}/functions/v1/off-platform-review-submit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token,
            rating,
            title: title.trim() || undefined,
            body: body.trim() || undefined,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        setSubmitError(result.error || 'Failed to submit review');
        setStatus('ready');
        return;
      }

      setStatus('success');
    } catch (error) {
      console.error('Error submitting review:', error);
      setSubmitError('Something went wrong. Please try again.');
      setStatus('ready');
    }
  };

  // Render loading state
  if (status === 'loading') {
    return (
      <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
        <div className="relative flex h-full w-full flex-1 flex-col bg-[#FFFCEF]">
          <header className="flex h-16 shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
            <div className="w-10" aria-hidden />
            <div className="flex flex-1 justify-center">
              <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
            </div>
            <div className="w-10" aria-hidden />
          </header>
          <main className="flex flex-1 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#304031] border-t-transparent" />
          </main>
        </div>
      </div>
    );
  }

  // Render error states
  if (status === 'invalid' || status === 'expired' || status === 'already_submitted' || status === 'error') {
    const errorConfig = {
      invalid: {
        title: 'Invalid Link',
        message: 'This review link is not valid. Please check your email and try again.',
        icon: (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
          />
        ),
      },
      expired: {
        title: 'Link Expired',
        message: 'This review link has expired. Please contact your guide if you would still like to leave a review.',
        icon: (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        ),
      },
      already_submitted: {
        title: 'Review Already Submitted',
        message: 'You have already submitted a review. Thank you for your feedback!',
        icon: (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        ),
      },
      error: {
        title: 'Something Went Wrong',
        message: 'We encountered an error loading this review. Please try again later.',
        icon: (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
          />
        ),
      },
    };

    const config = errorConfig[status];

    return (
      <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
        <div className="relative flex h-full w-full flex-1 flex-col bg-[#FFFCEF]">
          <header className="flex h-16 shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
            <div className="w-10" aria-hidden />
            <div className="flex flex-1 justify-center">
              <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
            </div>
            <div className="w-10" aria-hidden />
          </header>
          <main className="flex flex-1 min-h-0 flex-col items-center justify-center gap-6 overflow-y-auto px-6 pb-12 pt-6">
            <div className="flex max-w-md flex-col items-center gap-6 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#304031]/10">
                <svg
                  className="h-10 w-10 text-[#304031]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  {config.icon}
                </svg>
              </div>
              <h1 className="font-['Recoleta','Georgia',serif] text-[32px] font-normal leading-[1.1] text-[#304031]">
                {config.title}
              </h1>
              <p className="text-[15px] leading-relaxed text-[#214D3C]/80">
                {config.message}
              </p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Render success state
  if (status === 'success') {
    return (
      <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
        <div className="relative flex h-full w-full flex-1 flex-col bg-[#FFFCEF]">
          <header className="flex h-16 shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
            <div className="w-10" aria-hidden />
            <div className="flex flex-1 justify-center">
              <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
            </div>
            <div className="w-10" aria-hidden />
          </header>
          <main className="flex flex-1 min-h-0 flex-col items-center justify-center gap-6 overflow-y-auto px-6 pb-12 pt-6">
            <div className="flex max-w-md flex-col items-center gap-6 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                <svg
                  className="h-10 w-10 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="font-['Recoleta','Georgia',serif] text-[32px] font-normal leading-[1.1] text-[#304031]">
                Thank You!
              </h1>
              <p className="text-[15px] leading-relaxed text-[#214D3C]/80">
                Your review has been submitted successfully. Your feedback helps other seekers find the right guide for their journey.
              </p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Render review form
  return (
    <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
      <div className="relative flex h-full w-full flex-1 flex-col bg-[#FFFCEF]">
        <header className="flex h-16 shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
          <div className="w-10" aria-hidden />
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
          </div>
          <div className="w-10" aria-hidden />
        </header>

        <main className="flex flex-1 min-h-0 flex-col overflow-y-auto px-6 pb-12 pt-6">
          <div className="mx-auto w-full max-w-lg">
            <div className="mb-8 text-center">
              <h1 className="font-['Recoleta','Georgia',serif] text-[28px] font-normal leading-[1.1] text-[#304031]">
                Share Your Experience
              </h1>
              <p className="mt-3 text-[15px] leading-relaxed text-[#214D3C]/80 ph-mask">
                Hi {reviewInfo?.reviewer_name}, {reviewInfo?.guide_name} has requested your feedback about your experience working together.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              {/* Rating */}
              <div className="flex flex-col gap-3">
                <label className="text-sm font-medium text-[#304031]">
                  Overall Rating <span className="text-red-500">*</span>
                </label>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoveredRating(star)}
                      onMouseLeave={() => setHoveredRating(0)}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <svg
                        className={`h-10 w-10 ${
                          (hoveredRating || rating) >= star
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'fill-none text-[#304031]/30'
                        }`}
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                        />
                      </svg>
                    </button>
                  ))}
                </div>
                {rating > 0 && (
                  <p className="text-center text-sm text-[#214D3C]/60">
                    {rating === 5 && 'Excellent'}
                    {rating === 4 && 'Very Good'}
                    {rating === 3 && 'Good'}
                    {rating === 2 && 'Fair'}
                    {rating === 1 && 'Poor'}
                  </p>
                )}
              </div>

              {/* Title */}
              <div className="flex flex-col gap-2">
                <label htmlFor="title" className="text-sm font-medium text-[#304031]">
                  Review Title <span className="text-[#214D3C]/50">(optional)</span>
                </label>
                <input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Summarize your experience in a few words"
                  className="rounded-xl border border-[#304031]/20 bg-white px-4 py-3 text-[#304031] placeholder:text-[#304031]/40 focus:border-[#304031] focus:outline-none focus:ring-1 focus:ring-[#304031]"
                />
              </div>

              {/* Body */}
              <div className="flex flex-col gap-2">
                <label htmlFor="body" className="text-sm font-medium text-[#304031]">
                  Your Review <span className="text-[#214D3C]/50">(optional)</span>
                </label>
                <textarea
                  id="body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Share details about your experience..."
                  rows={5}
                  className="resize-none rounded-xl border border-[#304031]/20 bg-white px-4 py-3 text-[#304031] placeholder:text-[#304031]/40 focus:border-[#304031] focus:outline-none focus:ring-1 focus:ring-[#304031]"
                />
              </div>

              {/* Error Message */}
              {submitError && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  {submitError}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#304031] bg-[#304031] px-6 py-4 text-lg font-semibold text-[#FEF6D6] shadow-[0_16px_36px_-20px_rgba(48,64,49,0.55)] transition duration-200 hover:-translate-y-0.5 hover:bg-[#1F1924] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#304031] disabled:pointer-events-none disabled:opacity-50"
              >
                {status === 'submitting' ? (
                  <>
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#FEF6D6] border-t-transparent" />
                    Submitting...
                  </>
                ) : (
                  'Submit Review'
                )}
              </button>

              <p className="text-center text-xs text-[#214D3C]/60">
                Your review will be shared with {reviewInfo?.guide_name} and may be visible to other seekers.
              </p>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
}
